export type RoleToken =
  | 'nexsoss:platform_admin'
  | 'nexsoss:site_supervisor'
  | 'nexsoss:foh_operator'
  | 'nexsoss:service_operator'
  // L2/L3 platform support staff (hardening plan Phase 3). These come
  // from a SEPARATE ZITADEL org/project from customer tenant users and
  // are consumed by the standalone support console
  // (`apps/support-console/`), not this operator SPA -- included here
  // only because the role vocabulary is shared, per the plan's explicit
  // instruction. They bypass `UserSiteAssignment` for READ across every
  // site; every WRITE still resolves through the ordinary per-site
  // `RoleCapabilityPolicy` + the lifecycle broker's allowlist, so this
  // is a read-scope grant, not a privilege escalation.
  | 'nexsoss:support_l2'
  | 'nexsoss:support_l3';

export const ALL_ROLES: RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
  'nexsoss:service_operator',
  'nexsoss:support_l2',
  'nexsoss:support_l3',
];

// support_l2/support_l3 are ranked directly under platform_admin: they
// are an orthogonal privilege axis (cross-site platform access) rather
// than a site-operations role, so none of the operator-SPA-specific
// screens this ordering feeds (SideNav, TopBar, TasksScreen,
// AuditScreen, RoleGuardedElement) are expected to ever see them
// alongside a site role in practice -- included for completeness per
// the plan, not because a support_l2/l3 token is expected to reach this
// app's useHighestRole() in normal operation.
export const ROLE_PRECEDENCE: RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:support_l3',
  'nexsoss:support_l2',
  'nexsoss:site_supervisor',
  'nexsoss:service_operator',
  'nexsoss:foh_operator',
];

/**
 * Roles explicitly allowed to dispatch tasks. Mirrors the role list
 * used by `RoleGuard` around the dispatch form, the "+ New task"
 * button, and the modal trigger. Imported by `TasksScreen`,
 * `OverviewScreen`, and any future dispatch surface so the rule
 * lives in one place.
 */
export const TASK_DISPATCH_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
];

/**
 * Roles allowed to delete a finished task from history — deliberately
 * NARROWER than the dispatch list, and the omission is `foh_operator`:
 * creating work and erasing its record are different powers.
 *
 * Deleting removes the task everywhere it is recorded (the live tab's
 * projection, its completion row, its timeline); the support plane's
 * issue rows deliberately survive. It refuses non-terminal tasks
 * server-side — a live task can be cancelled, never deleted. Distinct
 * from `task.retire`, which *manufactures* terminal history for an
 * orphaned task and stays platform_admin-only.
 *
 * Mirrors `TASK_DELETE_HISTORY` (capability `task.delete_history`) in
 * the backend's `policy/capabilities.py`, which is authoritative; this
 * list only decides whether the control is drawn, and a mismatch shows
 * up as a button that 403s rather than as access nobody intended.
 */
export const TASK_DELETE_HISTORY_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles allowed to book a maintenance window — DELIBERATELY NOT the
 * dispatch list, and the omission is `foh_operator`.
 *
 * A window halts dispatch for the whole site and has no override, so
 * booking one is closer to raising fire mode than to booking a clean.
 * Mirrors `MAINTENANCE_MANAGE` in the backend's
 * `policy/capabilities.py`, which is authoritative; this list only
 * decides whether the control is drawn, and a mismatch shows up as a
 * button that 403s rather than as access nobody intended.
 */
export const MAINTENANCE_MANAGE_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles allowed to mark a job High priority (Akash, 2026-10-31) -- the
 * dispatch list without `foh_operator`. High goes ahead of every waiting
 * Normal job, other people's included, so it is the call of the roles
 * that already plan the site's day.
 *
 * A FIELD RULE, not a capability: the front desk still sends Normal jobs
 * exactly as before. Mirrors `HIGH_PRIORITY_ROLES` in the backend's
 * `task_priority.py`, which is authoritative and refuses High on every
 * write path; this list only decides whether the High choice is
 * offered, and a mismatch shows up as a refusal rather than as access
 * nobody intended.
 */
export const TASK_HIGH_PRIORITY_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles shown the Month view's plan check (Akash, 2026-10-31): everyone
 * who edits schedules (`TASK_DISPATCH_ROLES`) and everyone who reads
 * Metrics (`screens/Metrics/metrics-access.ts::METRICS_ROLES`), whose
 * numbers it shares. Spelled out rather than built from the two so the
 * api-server's list can be checked against it at a glance; a test pins
 * that it is exactly their union. Anyone else gets the Month grid
 * without it, and the plan check is never asked for on their behalf.
 */
export const PLAN_CHECK_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
  'nexsoss:service_operator',
];

/**
 * Roles allowed to switch a robot's automatic charging on and off.
 *
 * The same two roles that hold `robot.commission`, and deliberately
 * not `foh_operator`: switching it off also switches off the
 * low-battery retreat to the charger, so a robot left that way will
 * sit until it is flat. Whether front-of-house should hold it is an
 * open question, and answering it means editing this list, the
 * backend's `ROLE_TO_CAPABILITIES`, and the test that pins the role
 * set — on purpose, in one change.
 *
 * Mirrors `ROBOT_SET_IDLE_BEHAVIOR` in the backend's
 * `policy/capabilities.py`, which is authoritative; this list only
 * decides whether the control is drawn, and a mismatch shows up as a
 * button that 403s rather than as access nobody intended.
 */
export const ROBOT_IDLE_BEHAVIOR_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles allowed to ARM the lift write gate — and deliberately NOT the
 * lift-command list, which `LiftDetailDrawer`'s destination form gives
 * to `service_operator` as well.
 *
 * The omission is the whole point. Requesting a floor moves one lift,
 * once, under a session that expires. Arming hands the fleet an entire
 * guest lift bank in AGV mode for as long as it stays armed, and
 * nothing at any layer caps how many robots then call it — at Hotel
 * NoMad that is all six cars. So this sits with the maintenance-window
 * list rather than the dispatch one: it is closer to raising fire mode
 * than to booking a clean.
 *
 * Mirrors `LIFT_ARM_WRITES` in the backend's `policy/capabilities.py`,
 * which is authoritative; this list only decides whether the control
 * is drawn, and a mismatch shows up as a button that 403s rather than
 * as access nobody intended.
 */
export const LIFT_ARM_WRITES_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles allowed to block a lift from robot use, or travel between floors on it,
 * by hand or on the calendar, and to reset a tripped lift (Akash, 2026-11-02).
 * The front desk sees the state and no buttons. Mirrors `LIFT_RULE_SWITCH` and
 * `LIFT_RULE_CALENDAR` in `policy/capabilities.py`, which are authoritative.
 */
export const LIFT_RULE_SWITCH_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles allowed to ARM a door adapter's write gate — the same pair
 * `LIFT_ARM_WRITES_ROLES` grants, and deliberately so: "who may arm a
 * building system" gets one answer, not one per subsystem.
 *
 * Note what this is NOT. `DOOR_OVERRIDE_ROLES` below also holds
 * `support_l3`, because an override is an assertion about one door
 * that expires on its own. An arm is not scoped to a door at all: it
 * covers every door the adapter owns, for as long as it stays armed
 * (the confirm dialog names them for exactly that reason), and it is
 * the switch that decides whether the fleet can open a real door in a
 * live building. So it sits with the lift bank rather than with the
 * per-door primitives.
 *
 * Mirrors `DOOR_ARM_WRITES` in the backend's `policy/capabilities.py`
 * (capability `door.arm_writes`), which is authoritative; this list
 * only decides whether the control is drawn, and a mismatch shows up
 * as a button that 403s rather than as access nobody intended.
 */
export const DOOR_ARM_WRITES_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
];

/**
 * Roles allowed to DECLARE A DOOR MANUALLY OPEN and to revoke that
 * declaration -- and deliberately NOT the `door.open`/`door.close`
 * list, which `DoorDetailDrawer` gives to `service_operator` as well.
 *
 * The omission is the whole point. Open and Close ask a door to move
 * and are answered by the door: if the door does not move, nothing
 * happens. This one asks nothing of the door. It writes a record that
 * makes `rmf-door-state-mux` synthesize MODE_OPEN whatever the door is
 * actually doing, so RMF believes the door is open and drives a robot
 * through it. The operator is asserting an observation the platform
 * cannot make, which is why it sits with the supervisor.
 *
 * `support_l3` is listed for parity with the backend, which grants it
 * there (an L3 engineer's allowlisted primitive). Support staff work
 * in the standalone support console rather than this SPA, so in
 * practice this list draws the control for the two operator roles;
 * including L3 means a mismatch here can never be the thing that hides
 * a control the api-server would have allowed.
 *
 * Mirrors `DOOR_DECLARE_OPEN`/`DOOR_REVOKE_OVERRIDE` in the backend's
 * `policy/capabilities.py`, which is authoritative; this list only
 * decides whether the control is drawn, and a mismatch shows up as a
 * button that 403s rather than as access nobody intended.
 */
export const DOOR_OVERRIDE_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:support_l3',
];

/**
 * Roles allowed to confirm a dustbag has been emptied.
 *
 * The WIDEST of the write-role lists in this file, and the only
 * `robot.*` control drawn for `service_operator` and `foh_operator`.
 * The reasoning is in `policy/capabilities.py` and worth repeating:
 * the ack ends a wait, it starts no motion. A robot parked at a
 * DustBagStation waits (30 minutes by default) to be told the bag is empty,
 * and the person holding the bin liner is a service or FOH operator.
 * Withholding the ack from them buys no safety and strands a robot in
 * a corridor until somebody senior is free.
 *
 * Mirrors `ROBOT_CONFIRM_DUSTBAG` in the backend's
 * `policy/capabilities.py`, which is authoritative.
 */
export const ROBOT_CONFIRM_DUSTBAG_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
  'nexsoss:service_operator',
];

/**
 * Releasing a robot held after a fire alarm. As wide as the dustbag
 * ack above and for the same reason: during an evacuation the person
 * who can SEE the robot is the one who has to be able to release it,
 * and a hold only a supervisor can clear is a robot standing in a lift
 * lobby until somebody is found. The same list gates the hotel's
 * "Scanned it" press that ends an e-stop-in-a-lift pause (2026-10-12).
 * Mirrors the backend grant in `policy/capabilities.py`, which is
 * authoritative: this only decides who is SHOWN the button.
 */
export const ROBOT_FIRE_RELOCALIZE_ROLES: readonly RoleToken[] = [
  'nexsoss:platform_admin',
  'nexsoss:site_supervisor',
  'nexsoss:foh_operator',
  'nexsoss:service_operator',
];

export type AuthMode = 'zitadel' | 'mock';

export interface AuthenticatedUser {
  id: string;
  email: string;
  displayName: string;
  roles: RoleToken[];
  organisation: string;
}

export interface AuthContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AuthenticatedUser | null;
  roles: RoleToken[];
  token: string | null;
  login: (returnTo?: string) => Promise<void>;
  signInMockRole: (role: RoleToken) => void;
  /** Resolves to the path the completed sign-in should land on, when the
   *  provider knows one (the operator SPA's does; the consoles' resolve to
   *  nothing and keep their fixed landing). Callers pass it through
   *  `safeReturnTo` before navigating. */
  completeSignin: (url?: string) => Promise<string | void>;
  logout: () => Promise<void>;
  authMode: AuthMode;
}